// D&D Beyond -> DM Screen, live. The connector polls each watched character's
// record every 30 s and pushes DM_SCREEN_DDB_LIVE_EVENT when HP removed,
// temporary HP, HP override, conditions or death saves change. The DM Screen
// keeps max HP from its own sync and derives current HP from "removed".
import { newRequestId, request } from "../bridge";
import type { Character } from "./types";
import type { DdbHealthWriteResult, DdbLiveWatchResult, DdbSlotsWriteResult, DdbStatusWriteResult } from "./laneResults";

export interface DdbLiveState { removed: number; temp: number; override: number | null; conditions: string[]; exhaustion?: number; modifier?: number; deathSuccesses: number; deathFailures: number; modified?: string; uses?: { name: string; used: number; max: number }[]; subclasses?: string[]; slots?: { level: number; used: number }[] }
export interface DdbLiveEvent { characterId: string; sourceId: string; name: string; reason: string; live: DdbLiveState; previous: DdbLiveState | null }
export interface DdbLiveChange { characterId: string; characterName: string; changes: Partial<Pick<Character, "hp" | "maxHp" | "tempHp" | "conditions" | "exhaustion" | "deathSuccesses" | "deathFailures" | "maxHpModifier" | "maxHpBase" | "featureUses" | "spellSlotsUsed">>; featureUsed?: { name: string; used: number; max: number }[]; subclasses?: string[]; ddbModified?: string }

/** D&D Beyond's dateModified (carried on every live event) → ms, or null when
 *  absent/unparseable. Null = "recency unknown", so a caller then falls back to
 *  its pre-recency behaviour rather than guessing. ISO string or epoch-ms string. */
export function parseDdbModified(modified: string | undefined): number | null {
  if (!modified) return null;
  const iso = Date.parse(modified);
  if (Number.isFinite(iso)) return iso;
  const epoch = Number(modified);
  return Number.isFinite(epoch) && epoch > 0 ? epoch : null;
}
// Conditions D&D Beyond knows; anything else (Bloodied, a homebrew tag) lives only on the DM Screen.
export const DDB_CONDITIONS = ["Blinded", "Charmed", "Deafened", "Frightened", "Grappled", "Incapacitated", "Invisible", "Paralyzed", "Petrified", "Poisoned", "Prone", "Restrained", "Stunned", "Unconscious"];
const isDdbCondition = (n: string) => DDB_CONDITIONS.some((d) => d.toLowerCase() === n.toLowerCase());

// Absent is unknown, not zero. Number("") and Number(null) are both 0 AND finite, so a
// blank coerced with `|| 0` reaches the connector as a real 0 — which clicks the sheet's
// "--" and CLEARS a player's real exhaustion / death saves / Max-HP modifier. Send null for
// an absent value instead; the connector's write guards skip null. A real 0 still travels.
const absentNum = (v: unknown): number | null => {
  if (v === undefined || v === null || (typeof v === "string" && v.trim() === "")) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export async function watchDdbLive(roster: Character[], reconcile: boolean): Promise<{ ok: boolean; error?: string; watching?: number }> {
  // Absent is unknown here too, but THIS lane spells unknown as an absent key, not
  // null. The connector stores what we send verbatim (ddb-live.js:206) and its
  // reconcile escape asks `=== undefined` (:168), so a null would fall through to
  // `Number(null || 0)` and be read back as a real 0 — the escape would stay as dead
  // as the write-path guard was. Omitting the key makes it fire, and the SHEET's
  // value wins, which is the right direction on a read lane. maxHpModifier is never
  // sent and so already behaves this way: that is the proof the protocol works.
  const characters = roster.map((c) => {
    const exhaustion = absentNum(c.exhaustion);
    const deathSuccesses = absentNum(c.deathSuccesses);
    const deathFailures = absentNum(c.deathFailures);
    return {
      sourceId: c.id.replace(/^ddb-/, ""), name: c.name, hp: c.hp, maxHp: c.maxHp, tempHp: c.tempHp,
      conditions: (c.conditions || []).map((x) => typeof x === "string" ? x : String(x.name || x.condition || "")).filter(isDdbCondition),
      ...(exhaustion === null ? {} : { exhaustion }),
      ...(deathSuccesses === null ? {} : { deathSuccesses }),
      ...(deathFailures === null ? {} : { deathFailures }),
    };
  });
  const r = await request<DdbLiveWatchResult>({ type: "DM_SCREEN_DDB_LIVE_WATCH_REQUEST", requestId: newRequestId("ddb-live-watch"), characters, reconcile }, { resultType: "DM_SCREEN_DDB_LIVE_WATCH_RESULT", timeoutMs: 15000 });
  return { ok: Boolean(r.ok), error: r.error, watching: r.watching };
}

export function ddbLiveChange(roster: Character[], event: DdbLiveEvent): DdbLiveChange | null {
  const c = roster.find((x) => x.id === event.characterId);
  if (!c || !event.live) return null;
  const live = event.live;
  const changes: DdbLiveChange["changes"] = {};
  const modifier = Number(live.modifier || 0);
  // The base is the sheet's max without its modifier. First sight: derive it
  // from the max we hold (a sync's effective max) and the modifier the sheet
  // reports. Afterwards max = base + modifier, whichever side moved.
  let base = c.maxHpBase;
  if (base === undefined || !Number.isFinite(Number(base))) { base = Number(c.maxHp || 0) - (c.maxHpModifier !== undefined ? Number(c.maxHpModifier || 0) : modifier); changes.maxHpBase = base; }
  let maxHp = live.override !== null && live.override > 0 ? live.override : Math.max(1, Number(base) + modifier);
  if (maxHp !== Number(c.maxHp || 0)) changes.maxHp = maxHp;
  if (modifier !== Number(c.maxHpModifier || 0)) changes.maxHpModifier = modifier;
  // A live event with an absent/non-numeric `removed` or `temp` must not become NaN HP: that
  // would flow into c.hp and the health write sends `hp: c.hp` RAW (not through absentNum), so
  // NaN could reach a player's Roll20 bar / sheet. Skip the field when it isn't a finite number.
  const removed = Number(live.removed);
  if (Number.isFinite(removed)) { const hp = Math.max(0, maxHp - removed); if (hp !== c.hp) changes.hp = hp; }
  if (Number.isFinite(Number(live.temp)) && live.temp !== c.tempHp) changes.tempHp = live.temp;
  const currentNames = (c.conditions || []).map((x) => typeof x === "string" ? x : String(x.name || x.condition || ""));
  // Conditions only move when D&D Beyond's own set changed; DM-only tags
  // (Bloodied…) are kept alongside whatever the sheet now says.
  if (event.previous === null || event.previous.conditions.join("|") !== live.conditions.join("|")) {
    // Keep the DM Screen's own condition objects (durations) for names the
    // sheet still has; new sheet conditions come in as plain names.
    const existing = new Map((c.conditions || []).map((x) => [ (typeof x === "string" ? x : String(x.name || x.condition || "")).toLowerCase(), x ] as const));
    const merged = [...live.conditions.map((n) => existing.get(n.toLowerCase()) ?? n), ...(c.conditions || []).filter((x) => !isDdbCondition(typeof x === "string" ? x : String(x.name || x.condition || "")))];
    if (currentNames.filter(isDdbCondition).slice().sort().join("|").toLowerCase() !== live.conditions.slice().sort().join("|").toLowerCase()) changes.conditions = merged;
  }
  if (live.exhaustion !== undefined && live.exhaustion !== Number(c.exhaustion || 0)) changes.exhaustion = live.exhaustion;
  if (live.deathSuccesses !== (c.deathSuccesses || 0)) changes.deathSuccesses = live.deathSuccesses;
  if (live.deathFailures !== (c.deathFailures || 0)) changes.deathFailures = live.deathFailures;
  // Limited-use counters: a count that went up since the sheet's previous read
  // (or since the DM Screen last saw it) is a feature use.
  let featureUsed: DdbLiveChange["featureUsed"];
  if (Array.isArray(live.uses)) {
    const before = new Map<string, number>((event.previous?.uses || c.featureUses || []).map((u) => [u.name, u.used]));
    featureUsed = live.uses.filter((u) => before.has(u.name) && u.used > (before.get(u.name) as number));
    const known = (c.featureUses || []).map((u) => `${u.name}=${u.used}/${u.max}`).join("|");
    if (live.uses.map((u) => `${u.name}=${u.used}/${u.max}`).join("|") !== known) changes.featureUses = live.uses;
  }
  // Slots spent: kept so the card can show what the player has really used (they sometimes fudge it).
  if (Array.isArray(live.slots)) {
    const sig = (list: { level: number; used: number }[]) => list.map((s) => `${s.level}:${s.used}`).join(",");
    if (sig(live.slots) !== sig(c.spellSlotsUsed || [])) changes.spellSlotsUsed = live.slots;
  }
  return Object.keys(changes).length || featureUsed?.length ? { characterId: c.id, characterName: c.name, changes, featureUsed, subclasses: live.subclasses || [], ddbModified: live.modified } : null;
}

export function describeDdbLiveChange(x: DdbLiveChange, before: Character | undefined): string {
  const parts: string[] = [];
  if (x.changes.hp !== undefined) parts.push(`HP ${before?.hp ?? "?"} → ${x.changes.hp}`);
  if (x.changes.maxHp !== undefined) parts.push(`max HP ${before?.maxHp ?? "?"} → ${x.changes.maxHp}`);
  if (x.changes.tempHp !== undefined) parts.push(`temp HP ${before?.tempHp ?? "?"} → ${x.changes.tempHp}`);
  if (x.changes.conditions !== undefined) parts.push(x.changes.conditions.length ? `conditions: ${x.changes.conditions.join(", ")}` : "conditions cleared");
  if (x.changes.exhaustion !== undefined) parts.push(`exhaustion ${before?.exhaustion ?? 0} → ${x.changes.exhaustion}`);
  if (x.changes.maxHpModifier !== undefined && x.changes.maxHp === undefined) parts.push(`max HP modifier ${before?.maxHpModifier ?? 0} → ${x.changes.maxHpModifier}`);
  if (x.changes.deathSuccesses !== undefined || x.changes.deathFailures !== undefined) parts.push(`death saves ${x.changes.deathSuccesses ?? before?.deathSuccesses ?? 0}✓ ${x.changes.deathFailures ?? before?.deathFailures ?? 0}✗`);
  return parts.join(" · ");
}

// ---- DM Screen -> D&D Beyond -----------------------------------------------------
// HP and temporary HP written onto the sheet through the connector (an
// isolated tab drives the HP Management pane and reads it back). The
// connector tells its live poller what to expect first, so the write is not
// reported back as a change.
export async function writeDdbHealth(c: Character): Promise<{ ok: boolean; error?: string; hp?: number; tempHp?: number }> {
  const r = await request<DdbHealthWriteResult>({ type: "DM_SCREEN_DDB_HEALTH_WRITE_REQUEST", requestId: newRequestId("ddb-health"), sourceId: c.id.replace(/^ddb-/, ""), hp: c.hp, tempHp: c.tempHp, maxHp: c.maxHp }, { resultType: "DM_SCREEN_DDB_HEALTH_WRITE_RESULT", timeoutMs: 60000 });
  return { ok: Boolean(r.ok), error: r.error, hp: r.hp, tempHp: r.tempHp };
}

/** Tick spell slots on the player's real sheet (an isolated tab clicks the pips and reads the record back).
 *  `used` is the NEW number spent at that level, not a change. Refused for a character that is not from D&D Beyond. */
export async function writeDdbSlots(c: Character, slots: { level: number; used: number }[]): Promise<{ ok: boolean; error?: string; slots?: { level: number; used: number }[] }> {
  if (!c.id.startsWith("ddb-")) return { ok: false, error: "Only a D&D Beyond character has spell slots to write" };
  const r = await request<DdbSlotsWriteResult>({ type: "DM_SCREEN_DDB_SLOTS_WRITE_REQUEST", requestId: newRequestId("ddb-slots"), sourceId: c.id.replace(/^ddb-/, ""), slots }, { resultType: "DM_SCREEN_DDB_SLOTS_WRITE_RESULT", timeoutMs: 190000 });
  return { ok: Boolean(r.ok), error: r.error, slots: r.slots };
}

export type StatusGroup = "health" | "conditions" | "deathSaves" | "maxHp";
export async function writeDdbStatus(c: Character, fields: StatusGroup[]): Promise<{ ok: boolean; error?: string; wrote?: string[]; skipped?: string[] }> {
  const conditions = (c.conditions || []).map((x) => typeof x === "string" ? x : String(x.name || x.condition || "")).filter(isDdbCondition);
  const r = await request<DdbStatusWriteResult>({ type: "DM_SCREEN_DDB_STATUS_WRITE_REQUEST", requestId: newRequestId("ddb-status"), sourceId: c.id.replace(/^ddb-/, ""), fields, hp: c.hp, tempHp: c.tempHp, maxHp: c.maxHp, conditions, exhaustion: absentNum(c.exhaustion), deathSuccesses: absentNum(c.deathSuccesses), deathFailures: absentNum(c.deathFailures), maxHpModifier: absentNum(c.maxHpModifier) }, { resultType: "DM_SCREEN_DDB_STATUS_WRITE_RESULT", timeoutMs: 120000 });
  return { ok: Boolean(r.ok), error: r.error, wrote: r.wrote, skipped: r.skipped };
}
export function statusSignature(c: Character): Record<StatusGroup, string> {
  const conditions = (c.conditions || []).map((x) => typeof x === "string" ? x : String(x.name || x.condition || "")).filter(isDdbCondition).map((n) => n.toLowerCase()).sort().join(",");
  return { health: `${c.hp}/${c.tempHp}`, conditions: `${conditions}#${Number(c.exhaustion || 0)}`, deathSaves: `${Number(c.deathSuccesses || 0)}/${Number(c.deathFailures || 0)}`, maxHp: String(Number(c.maxHpModifier || 0)) };
}

// ---- attacks from the table ---------------------------------------------------
// A hit applied from a Roll20 card: temp HP absorbs first; a max-HP drain
// lowers the max and the sheet's modifier by the same amount so a long rest
// (Restore max HP) can undo exactly what was applied.
export function applyHit(c: Character, damage: number, drainMax: boolean): Partial<Character> {
  const dmg = Math.max(0, Math.round(damage));
  let temp = Number(c.tempHp || 0), hp = Number(c.hp || 0), maxHp = Number(c.maxHp || 0);
  const fromTemp = Math.min(temp, dmg); temp -= fromTemp; hp = Math.max(0, hp - (dmg - fromTemp));
  const out: Partial<Character> = { hp, tempHp: temp };
  if (drainMax && dmg > 0) {
    maxHp = Math.max(1, maxHp - dmg);
    out.maxHp = maxHp; out.hp = Math.min(hp, maxHp);
    out.maxHpModifier = modifierFor(c, maxHp);
    out.maxHpReduction = Number(c.maxHpReduction || 0) + dmg;
  }
  return out;
}
export function restoreMaxHp(c: Character): Partial<Character> {
  const back = Number(c.maxHpReduction || 0);
  if (!back) return {};
  const maxHp = Number(c.maxHp || 0) + back;
  return { maxHp, maxHpModifier: modifierFor(c, maxHp), maxHpReduction: 0 };
}
/** The sheet modifier that yields this max: base + modifier = max when the base is known, else a delta. */
export function modifierFor(c: Character, maxHp: number): number {
  const base = c.maxHpBase;
  if (base !== undefined && Number.isFinite(Number(base))) return maxHp - Number(base);
  return Number(c.maxHpModifier || 0) + (maxHp - Number(c.maxHp || 0));
}
// Attacks whose hit lowers the target's hit point maximum by the damage dealt.
export const MAX_HP_DRAIN = /life drain|grave touch|blood drain|enervat|drain(?:ing)? (?:touch|bite|kiss|strike)|soul (?:drain|rend)|withering touch/i;

/* WHAT THE SHEET IS KNOWN TO HOLD, per character, per status group.
 *
 * This replaces a flag that said "this character's last change came from the
 * sheet, so skip the write-back once". That flag asked a question about HISTORY
 * — where did this change come from? — and history is exactly what is lost when
 * an event arrives out of order, is dropped, or is never consumed. It failed
 * both ways at the table:
 *
 *   It over-suppressed. One flag per CHARACTER, consumed by a single `continue`
 *   that skipped all four groups, so a poll carrying an HP change threw away a
 *   condition change the DM had made in the same tick.
 *
 *   It leaked. The flag was also set for a SPELLBOOK update, which is not a
 *   status group at all, so nothing ever consumed it. It sat in the set as a
 *   landmine, and the next genuine local condition change was discarded in
 *   silence. That is why a condition cleared here came straight back: the clear
 *   never reached the sheet, and the next poll restored it.
 *
 * So ask about STATE instead: does the sheet currently hold what we hold? There
 * is no flag to leak and no ordering to get wrong, and it is self-healing — if
 * the two ever drift for a reason nobody predicted, the next comparison notices.
 *
 * The values stored are the same strings `statusSignature` produces, and they
 * are only ever recorded from something the SHEET confirmed: a poll we applied,
 * an import, or a write that `writeStatus` read back and verified.
 */
const sheetHolds = new Map<string, Partial<Record<StatusGroup, string>>>();
/** The last signature we ATTEMPTED per character, so a failing write is retried
 *  when the value changes rather than on every render for ever. */
const attempted = new Map<string, string>();

/** Record what the sheet is now known to hold. Omit `groups` for all of them. */
export function noteSheetHolds(id: string, sig: Record<StatusGroup, string>, groups?: StatusGroup[]): void {
  const have = sheetHolds.get(id) || {};
  for (const g of groups || (Object.keys(sig) as StatusGroup[])) have[g] = sig[g];
  sheetHolds.set(id, have);
}

/** Forget a character entirely (removed from the roster, campaign switched). */
export const forgetSheetHolds = (id: string): void => { sheetHolds.delete(id); attempted.delete(id); };

/**
 * Which groups differ from what the sheet is known to hold, and so need writing.
 *
 * A character we have never seen is SEEDED rather than written: on first sight
 * we have no idea what the sheet holds, and assuming it disagrees would push the
 * DM Screen's opening state onto every sheet at load. That was the old code's
 * `if (!previous) return`, kept deliberately.
 */
export function groupsToWrite(id: string, sig: Record<StatusGroup, string>): StatusGroup[] {
  const have = sheetHolds.get(id);
  if (!have) { noteSheetHolds(id, sig); return []; }
  const all = Object.keys(sig) as StatusGroup[];
  const differ = all.filter((g) => have[g] !== undefined && have[g] !== sig[g]);
  for (const g of all) if (have[g] === undefined) have[g] = sig[g];   // a group we have never seen: seed, do not write
  if (!differ.length) return [];
  const whole = all.map((g) => sig[g]).join("|");
  if (attempted.get(id) === whole) return [];   // already tried this exact state and it did not take
  attempted.set(id, whole);
  return differ;
}

/** A write that read back clean: the sheet now holds these values. */
export function confirmWritten(id: string, sig: Record<StatusGroup, string>, groups: StatusGroup[]): void {
  noteSheetHolds(id, sig, groups);
  attempted.delete(id);
}
